import { useStore } from 'effector-react'
import { useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  $lastServices,
  $savingPhotosCount,
  savePhoto,
} from '~/entities/Inspection/model'
import {
  replacingHeadlightsOptions,
  replacingWipersOptions,
} from '~/shared/config/constants'
import {
  InspectionPhotoTypeEnum,
  InspectionServiceTypeEnum,
} from '~/shared/config/enums'
import { formatDateForUI } from '~/shared/lib/date'
import { Button } from '~/shared/ui/Buttons'
import {
  ModalButtonCheckboxes,
  ModalButtonRange,
  Camera,
  FormProvider,
} from '~/shared/ui/form'
import { Card, CardDescription, CardTitle, Wrapper } from './styled'
import { CarUnderHoodInspectionFormValues } from './types'

type CarUnderHoodInspectionFormProps = {
  onSuccess: () => void
  form: UseFormReturn<CarUnderHoodInspectionFormValues>
}

export function CarUnderHoodInspectionForm({
  onSuccess,
  form,
}: CarUnderHoodInspectionFormProps) {
  const { t } = useTranslation()

  const savingPhotoCount =
    useStore($savingPhotosCount)[InspectionPhotoTypeEnum.LAST_TO]
  const isPhotoPending = savingPhotoCount > 0

  const lastServices = useStore($lastServices)

  const getLastServicesDateByType = useCallback(
    (type: InspectionServiceTypeEnum) => {
      const foundLastServices = lastServices?.find(
        (service) => service.type === type,
      )
      return foundLastServices?.createdAt
        ? formatDateForUI(foundLastServices.createdAt)
        : null
    },
    [lastServices],
  )

  const motorOilLastDate = getLastServicesDateByType(
    InspectionServiceTypeEnum.MOTOR_OIL,
  )
  const antifreezeLastDate = getLastServicesDateByType(
    InspectionServiceTypeEnum.ANTIFREEZE,
  )
  const wipersLastDate = getLastServicesDateByType(
    InspectionServiceTypeEnum.WIPERS,
  )
  const lampsLastDate = getLastServicesDateByType(
    InspectionServiceTypeEnum.LAMPS,
  )

  return (
    <FormProvider form={form} onSuccess={onSuccess}>
      <Wrapper>
        <Card>
          <div>
            <CardTitle>{t('form.oil top-up')}</CardTitle>
            {motorOilLastDate && (
              <>
                <CardDescription>{t('form.last action')}</CardDescription>
                <CardDescription>{motorOilLastDate}</CardDescription>
              </>
            )}
          </div>
          <ModalButtonRange
            title={t('form.oil top-up')}
            buttonTitle={t('form.add')}
            description={t('form.select the amount in ml to be added')}
            suffix={` ${t('form.ml')}`}
            step={50}
            name={InspectionServiceTypeEnum.MOTOR_OIL}
          />
        </Card>

        <Card>
          <div>
            <CardTitle>{t('form.coolant top-up')}</CardTitle>
            {antifreezeLastDate && (
              <>
                <CardDescription>{t('form.last action')}</CardDescription>
                <CardDescription>{antifreezeLastDate}</CardDescription>
              </>
            )}
          </div>
          <ModalButtonRange
            title={t('form.coolant top-up')}
            buttonTitle={t('form.add')}
            description={t('form.select the amount in ml to be added')}
            suffix={` ${t('form.ml')}`}
            step={50}
            name={InspectionServiceTypeEnum.ANTIFREEZE}
          />
        </Card>

        <Card>
          <div>
            <CardTitle>{t('form.wiper replacement')}</CardTitle>
            {wipersLastDate && (
              <>
                <CardDescription>{t('form.last action')}</CardDescription>
                <CardDescription>{wipersLastDate}</CardDescription>
              </>
            )}
          </div>
          <ModalButtonCheckboxes
            title={t('form.wiper replacement')}
            buttonTitle={t('form.add')}
            description={t('form.specify the wiper to be replaced')}
            name={InspectionServiceTypeEnum.WIPERS}
            optionsI18n={replacingWipersOptions}
          />
        </Card>

        <Card>
          <div>
            <CardTitle>{t('form.bulb replacement')}</CardTitle>
            {lampsLastDate && (
              <>
                <CardDescription>{t('form.last action')}</CardDescription>
                <CardDescription>{lampsLastDate}</CardDescription>
              </>
            )}
          </div>
          <ModalButtonCheckboxes
            title={t('form.bulb replacement')}
            buttonTitle={t('form.add')}
            description={t(
              'form.specify the headlight where the bulb needs replacement',
            )}
            name={InspectionServiceTypeEnum.LAMPS}
            optionsI18n={replacingHeadlightsOptions}
          />
        </Card>

        <div style={{ padding: '16px 0' }}>
          <Camera
            buttonTitle='form.add photo of maintenance tag'
            name='lastTOPhotos'
            saveFileFn={(file) =>
              savePhoto({
                file,
                type: InspectionPhotoTypeEnum.LAST_TO,
                form,
                fieldName: 'lastTOPhotos',
              })
            }
            isLoading={isPhotoPending}
          />
        </div>

        <Button
          fullWidth
          type='submit'
          variant='limeFlooded'
          uppercase
          style={{ marginTop: '16px' }}
        >
          {t('button.next')}
        </Button>
      </Wrapper>
    </FormProvider>
  )
}
